import { usersKeys } from "@/query-keys";
import { UserSchema, type User } from "@/schemas/userSchema";
import { services } from "@/services";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { createContext, useContext, useMemo } from "react";

export interface IAuthContext {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
  refetchAuth: () => Promise<void>;
}

const AuthContext = createContext<IAuthContext | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: usersKeys.profile(),
    queryFn: async () => {
      try {
        const response = await services.getProfile();
        return UserSchema.parse(response);
      } catch (error) {
        console.error("Auth validation failed:", error);
        return null;
      }
    },
    staleTime: 1000 * 60 * 10,
  });

  const logoutMutation = useMutation({
    mutationFn: services.logout,
    onSuccess: () => {
      queryClient.setQueryData(usersKeys.profile(), null);

      queryClient.clear();
    },
  });

  const value = useMemo(
    () => ({
      user: data ?? null,
      isAuthenticated: !!data,
      isLoading,
      logout: logoutMutation.mutate,
      refetchAuth: async () => {
        await refetch();
      },
    }),
    [data, isLoading, logoutMutation.mutate, refetch],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
